/**
 * @prettier
 * @author Alexander Nagelberg
 * @since 24.3
 */
//@ts-check
import React from 'react'
import { connect } from 'react-redux'
import { Field, InjectedFormProps, getFormValues, reduxForm } from 'redux-form'
import { MdDone } from 'react-icons/md'
import { submitOptions } from '../actions/settingsActions'
import LgyTextArea from 'app/components/util/lgy-text-area/LgyTextArea'
import LgyInputField from 'app/components/util/lgy-input-field/LgyInputField'
import LgyCheckbox from 'app/components/util/lgy-checkbox/LgyCheckbox'
import LgySelectField from 'app/components/util/lgy-select-field/LgySelectField'

import { IFieldMultiChoice } from '../types/IFieldMultiChoice'
import { SettingFieldType } from '../types/SettingFieldType'
import { ISettingOption } from '../types/ISettingOption'
import { IUpdateSettingsRequest } from '../types/IUpdateSettingsRequest'
import { ISettingsHeader } from '../types/ISettingsHeader'
import { ISettingsSetting } from '../types/ISettingsSetting'

import '../style/settingsStyle.scss'

const minLength25 = (str?: string) =>
  typeof str === 'string' && str.length < 25 ? 'New Note Required to be at least 25 characters' : undefined

export interface INoteFormValue {
  note?: string
}

export interface IFormValue {
  [key: string]: string | boolean | INoteFormValue
}

interface IProps {
  formValues?: IFormValue
  headers?: Array<ISettingsHeader>
  settings?: Array<ISettingsSetting>
  submitOptions?: (request: IUpdateSettingsRequest) => Promise<void>
}

/**
 * Simple component for displaying field based on field type
 */
const SwitchField = ({
  option: { id, fieldType, placeholder, readOnly, value },
  arialabelledby
}: {
  option: ISettingOption
  arialabelledby?: string
}) => {
  switch (fieldType) {
    case SettingFieldType.StaticText:
      return <>{value}</>
    case SettingFieldType.TextArea:
      return (
        <Field
          component={LgyTextArea}
          name={id}
          disabled={readOnly}
          placeholder={placeholder}
          maxLength={1000}
          required
          aria-labelledby={arialabelledby}
        />
      )
    case SettingFieldType.TextBox:
      return (
        <Field
          component={LgyInputField}
          name={id}
          disabled={readOnly}
          placeholder={placeholder}
          required
          aria-labelledby={arialabelledby}
        />
      )
    case SettingFieldType.CheckBox:
      return (
        <Field
          component={LgyCheckbox}
          name={id}
          disabled={readOnly}
          placeholder={placeholder}
          required
          aria-labelledby={arialabelledby}
        />
      )
    case SettingFieldType.MultiChoice:
      return (
        <Field component={LgySelectField} name={id} disabled={readOnly} required aria-labelledby={arialabelledby}>
          {JSON.parse(placeholder)?.map(({ value, display }: IFieldMultiChoice, idx: number) => (
            <option key={idx} value={value}>
              {display}
            </option>
          ))}
        </Field>
      )
    default:
      return <></>
  }
}

const SettingsTable = ({
  formValues,
  headers,
  handleSubmit,
  settings,
  pristine,
  submitting,
  submitOptions
}: IProps & InjectedFormProps) => (
  <table role='table' className='lgy-settings-table'>
    <thead>
      <tr>
        {headers?.map(({ title, order }) => (
          <th role='columnheader' key={order} id={`header-${order}`}>
            {title}
          </th>
        ))}
        <th id='header-submit'>Submit</th>
      </tr>
    </thead>
    <tbody>
      {settings
        ?.sort(({ id: a }, { id: b }) => a?.localeCompare(b) ?? 0)
        ?.map(({ options, id }, rowIndex) => {
          const notesCells = Math.round((options.length + 1) / 2)
          const notesOffset = Math.round(notesCells / 2)
          const notesPostCells = options.length - notesCells - notesOffset + 1

          const { note } = (formValues ? formValues[id] ?? {} : {}) as INoteFormValue

          return (
            <React.Fragment key={rowIndex}>
              <tr aria-labelledby={`row-${rowIndex}`}>
                {options.map(({ id, ...other }, colIndex) => (
                  <td key={id} role='cell'>
                    <SwitchField option={{ id, ...other }} aria-labelledby={`header-${colIndex}`} />
                  </td>
                ))}
                <td role='cell'>
                  <button
                    disabled={submitting || pristine || !note || note?.length < 25}
                    aria-label='Submit'
                    onClick={handleSubmit(() => {
                      const request = {
                        id,
                        note: note ?? '', // note should fail validation before this and not end up null
                        options: options
                          ?.filter(({ id }) => formValues && formValues[id] != null)
                          ?.filter(({ fieldType, readOnly }) => fieldType !== SettingFieldType.StaticText && !readOnly)
                          ?.map(({ id }) => ({ id, value: String(formValues ? formValues[id] : '') }))
                      }

                      if (submitOptions) return submitOptions(request)
                    })}
                  >
                    <MdDone color='white' size='20' />
                  </button>
                </td>
              </tr>
              <tr>
                {Array.apply(null, Array(notesOffset)).map((_, idx) => (
                  <td key={idx} />
                ))}
                <td colSpan={notesCells}>
                  <div className='lgy-textarea-notes'>
                    <Field
                      component={LgyTextArea}
                      name={`${id}.note`}
                      maxLength={1000}
                      label='Note'
                      required
                      validate={[minLength25]}
                    />
                  </div>
                </td>
                {Array.apply(null, Array(notesPostCells)).map((_, idx) => (
                  <td key={idx} />
                ))}
              </tr>
            </React.Fragment>
          )
        })}
    </tbody>
  </table>
)

const mapStateToProps = (state: any) => ({ formValues: getFormValues('settings')(state) })

export default connect<{}, {}, IProps>(mapStateToProps, { submitOptions })(
  reduxForm({ form: 'settings', enableReinitialize: true })(SettingsTable)
)
